import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthenticatedMediaService {
    private readonly _http = inject(HttpClient);
    private readonly _cache = new Map<string, string>();
    private readonly _inflight = new Map<string, Observable<string>>();

    resolve(url: string | null | undefined): Observable<string> {
        const trimmed = (url ?? '').trim();
        if (!trimmed) {
            return of('');
        }
        if (!this.isApiHosted(trimmed)) {
            return of(trimmed);
        }

        const cached = this._cache.get(trimmed);
        if (cached) {
            return of(cached);
        }

        const existing = this._inflight.get(trimmed);
        if (existing) {
            return existing;
        }

        const request$ = this._http
            .get(trimmed, {
                responseType: 'blob',
            })
            .pipe(
                map((blob: Blob): string => {
                    const objectUrl = URL.createObjectURL(blob);
                    this._cache.set(trimmed, objectUrl);
                    return objectUrl;
                }),
                catchError((): Observable<string> => of('')),
                finalize((): void => {
                    this._inflight.delete(trimmed);
                }),
                shareReplay(1)
            );

        this._inflight.set(trimmed, request$);
        return request$;
    }

    /**
     * Authenticated blob GET without object-URL caching.
     * Use when the caller needs to inspect/retype the blob (Document View MIME branch)
     * instead of binding a cached `blob:` URL to an `<img>`/`iframe`.
     */
    fetchBlob(url: string | null | undefined): Observable<Blob | null> {
        const trimmed = (url ?? '').trim();
        if (!trimmed) {
            return of(null);
        }

        return this._http.get(trimmed, { responseType: 'blob' }).pipe(
            catchError((): Observable<null> => of(null))
        );
    }

    /** True when `resolve()` can return a blob object URL synchronously from cache. */
    hasCached(url: string): boolean {
        return this._cache.has(url.trim());
    }

    isApiHosted(url: string): boolean {
        try {
            const parsed = new URL(url, window.location.origin);
            const apiBase = environment.apiLink.endsWith('/')
                ? environment.apiLink
                : `${environment.apiLink}/`;
            const api = new URL(apiBase);
            return parsed.hostname === api.hostname;
        } catch {
            return false;
        }
    }
}
